/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 7/10/2020
 */

import {Type} from "class-transformer";
import {RoleEnum} from "@/core/js/model/Permissions/RoleEnum.js";
import {SchedulerSettings} from "@/core/js/model/scheduler/SchedulerSettings";
import DbBaseObject from "@shared/DbBaseObject";
import CurrencyItem from "@shared/Profile/CurrencyItem";
import IntegrationsList from "@shared/Profile/IntegrationsList";
import ExtraProps from "@shared/Profile/ExtraProps";
import BrandingSettingsItem from "@/core/modules/coach/Profile/Branding/Model/BrandingSettingsItem";
import QwoachFile from "@shared/Drive/QwoachFile";
import AddressItem from "@shared/Profile/AddressItem";
import {CoachType} from "@shared/Profile/CoachType";
import TimeZoneItem from "@shared/Profile/TimeZoneItem";
import AccessRules from "@shared/Profile/AccessRules.js";
import PortalRef from "@shared/Profile/PortalRef.js";
import AccessRuleItem from "@shared/Profile/AccessRuleItem.js";
import ClientProfileRef from "./ClientProfileRef.js";
const {plainToClass} = require("@qwoach/class-transformer-wrapper");


export default class UserProfile extends DbBaseObject{

	name: string = ""
	firstName: string = ""
	lastName: string = ""
	email: string = ""
	primaryPhone: string = null
	secondaryPhone: string = null
	note: string = ""
	role: string = ""
	@Type(() => AccessRules)
	accessRules: AccessRules = new AccessRules() //readonly
	appVersion: number = 2

	lastActiveTs: number = 0

	@Type(() => TimeZoneItem)
	timeZoneItem: TimeZoneItem = null

	pictureUrl: string = ""

	@Type(() => AddressItem)
	primaryAddress: AddressItem = new AddressItem()

	@Type(() => QwoachFile)
	userPicture: QwoachFile = null

	@Type(() => QwoachFile)
	logo: QwoachFile = null

	@Type(() => ExtraProps)
	extraProps: ExtraProps = new ExtraProps()

	@Type(() => IntegrationsList)
	integrationsList: IntegrationsList = new IntegrationsList()

	@Type(() => CurrencyItem)
	currencyItem: CurrencyItem = CurrencyItem.USD
	// currencyItem: CurrencyItem = new CurrencyItem("EUR", "€")

	// isRecoveryCoach: Boolean = true //legacy field. Deprecated since schema v4. appVersion is used instead

	coachType: CoachType = CoachType.IAOTRC

	@Type(() => SchedulerSettings)
	schedulerSettings: SchedulerSettings = new SchedulerSettings();

	@Type(() => ClientProfileRef)
	clientProfileRef: ClientProfileRef[] = []; //deprecated since schema v4. The relevant data was moved to accessRules

	@Type(() => ClientProfileRef)
	portalRef: ClientProfileRef = null; //deprecated since schema v4. The relevant data was moved to accessRules


	currentPortalRef: PortalRef = null;
	currentOwnerId: string = null
	currentCoachId: string = null


	@Type(() => BrandingSettingsItem)
	brandingSettings: BrandingSettingsItem = new BrandingSettingsItem()

	clientOptedInAsCoach: boolean = false

	featuresUsing = {}


	constructor(name: string = "", email: string = "", id: string = "") {
		super();
		this.name = name;
		this.email = email?.toLocaleLowerCase().trim() || "";
		this.id = id;
		this.currentCoachId = this.currentCoachId || this.currentOwnerId
		this.currentOwnerId = this.currentOwnerId || this.currentCoachId
	}

	getCurrentSchemaVersion() {
		return 5
	}

	isTeamEnabled(){
		return this.featuresUsing.team === true
	}

	getMyIdForOwner(ownerId){
		let accessRuleItem = this.getCurrentOwnerAccessRuleItem(ownerId) || {}
		return accessRuleItem.myIdForOwner || this.id
	}

	setAccessRulesFor(ownerId, accessRuleItem){
		this.accessRules.setAccessRuleFor(ownerId, accessRuleItem)
	}
	getAccessRuleItemFor(ownerId){
		return this.accessRules[ownerId]
	}
	removeAccessRulesFor(ownerId, accessRuleItem){
		this.accessRules.removeAccessRuleFor(ownerId, accessRuleItem)
	}
	hasAccessRulesFor(ownerId){
		return this.accessRules[ownerId] !== undefined
	}

	setPortalRef(portalRef){
		this.currentPortalRef = portalRef
		this.currentOwnerId = portalRef.ownerId
		this.role = portalRef.role
	}

	isPortalSet(){
		return this.currentPortalRef &&
			this.currentOwnerId &&
			this.currentPortalRef.ownerId === this.currentOwnerId &&
			this.currentPortalRef.role === this.role &&
			this.currentPortalRef.userId != null
	}

	//ownerId is optional and overrides currentOwnerId if provided
	getCurrentOwnerAccessRuleItem(ownerId){
		let id = ownerId || this.currentOwnerId || this.currentCoachId //can potentially point to self -> accessRules will be undefined

		let accessRuleItem = this.accessRules[id]
		return plainToClass(AccessRuleItem, accessRuleItem)
	}

	//ownerId is optional and overrides currentOwnerId if provided
	getUserIdForOwner(ownerId){
		let accessRuleItem = this.getCurrentOwnerAccessRuleItem(ownerId)
		console.log("UserProfile.getUserIdForOwner accessRuleItem = ", accessRuleItem)
		return accessRuleItem?.myIdForOwner || this.id
	}

	addClientProfileRef(clientProfileRef){
		if(clientProfileRef){
			this.clientProfileRef.pushUnique(clientProfileRef, ref => ref.coachId === clientProfileRef.coachId)
		}
	}

	isEmailConnected(){
		return this.integrationsList.isEmailIntegrated()
	}

	getBrandedLink(link){
		return this.brandingSettings ? this.brandingSettings.getBrandedLink(link) : link
	}

	isCustomPortalEnabled(){
		return this.brandingSettings ? this.brandingSettings.isConnected() : false
	}

	hasBrandedHostUrl(){
		return this.brandingSettings && this.brandingSettings.hasBrandedHostUrl()
	}

	getPrimaryColor(){
		return this.isCustomStylingEnabled() ? this.brandingSettings.primaryColor : null
	}
	isCustomStylingEnabled(){
		return this.brandingSettings && this.brandingSettings.isCustomStyleEnabled
	}


	isOwner() {
		return this.currentOwnerId === this.id
	}

	isCoach(){
		return this.role === RoleEnum.COACH
	}

	isClient(){
		return this.role === RoleEnum.CLIENT
	}

	getOwnerId(){
		return this.currentOwnerId
	}

	getCoachId(){
		if(this.currentOwnerId){
			return this.currentOwnerId
		}
		if(this.isCoach() || this.isOwner()){
			return this.id
		}
		return null
		//rest is deprecated
		// if(this.currentCoachId){
		// 	return this.currentCoachId;
		// }
		// if(this.portalRef){
		// 	return this.portalRef.coachId
		// }
		// let clientRef = this.clientProfileRef[0]
		//
		// return (clientRef || {}).coachId || null
	}

	getClientProfileIdByCoachId(coachId){
		if(this.isCoach()){
			return
		}
		if(this.currentPortalRef){
			return this.currentPortalRef.userId
		}

		let ref = this.clientProfileRef.find(ref => ref.coachId === coachId)
		if(ref){
			return ref.clientProfileIdForCoach
		}
		if(this.portalRef){
			return this.portalRef.clientProfileIdForCoach
		}
	}


	getFirstName(){
		this.setupFirstLastNameFromFullNameIfNeeded()
		return this.firstName
	}

	setupFirstLastNameFromFullNameIfNeeded(){
		if(this.name){// && !this.firstName && !this.lastName){
			let [firstName, ...lastName] = this.name.split(" ")
			this.firstName = firstName
			this.lastName = (lastName || []).join(" ")
			this.lastName_casefolded = this.caseFoldNormalize(this.lastName)
		}
	}

	isIAOTRCCoach(){
		return this.coachType === CoachType.IAOTRC
	}
	isTraumaCoach(){
		return this.coachType === CoachType.TRAUMA_COACH
	}
	isFamilyTherapyCoach(){
		return this.coachType === CoachType.FAMILY_COACH
	}
	isAddictionCoach(){
		return this.coachType === CoachType.ADDICTION_COACH
	}

	getTimeZoneId(){
		return this.timeZoneItem ? this.timeZoneItem.timeZoneId : null
	}
	getTimeZoneName(){
		return this.timeZoneItem ? this.timeZoneItem.timeZoneName : null
	}


	migrateSchema(oldVersion, newVersion) {
		if(!oldVersion){
			return
		}
		super.migrateSchema(oldVersion, newVersion)
		// console.log("UserProfile.migrateSchema this.clientProfileRef = ", JSON.parse(JSON.stringify(this.clientProfileRef)), this)
		if(oldVersion < 2){
			if(!this.portalRef && this.clientProfileRef.length > 0){
				this.portalRef = this.clientProfileRef[0]
			}
		}
		if(oldVersion < 3){
			if((!this.timeZoneItem || !this.timeZoneItem.timeZoneId) && this.schedulerSettings && this.schedulerSettings.timeZone){
				this.timeZoneItem = new TimeZoneItem(this.schedulerSettings.timeZone)
			}
		}
		if(oldVersion < 4){
			if(!this.isRecoveryCoach){
				this.appVersion = 1
			}
		}
		if(oldVersion < 5){
			//this migration is performed on server side - it handles transition from clientProfileRef to accessRules
			// if(this.portalRef){
			// 	let accessRuleItem = new AccessRuleItem()
			// 	accessRuleItem.myIdForOwner = this.portalRef.clientProfileIdForCoach
			// 	accessRuleItem.roles = [this.role || RoleEnum.COACH]
			// 	accessRuleItem.clearanceLevel = ClearanceLevelEnum.ALL
			// 	this.accessRules.setAccessRuleFor(this.portalRef.coachId, accessRuleItem)
			// }
		}
	}

	static fromClientProfile(clientProfile){
		let userProfile = new UserProfile(clientProfile.name, clientProfile.email, clientProfile.clientId)
		userProfile.role = RoleEnum.CLIENT
		return userProfile
	}

}
